import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, from, of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import { Router } from '@angular/router'
import * as bcrypt from 'bcryptjs'
import message, { iMessage } from '../data/errorMessage'
import { environmentURL } from 'src/environments/environments'
import { USER_TYPE_INTERNO_SDM } from '../core/auth-constants'
import { InactivityTimeoutService } from './inactivity-timeout.service'
import { EventosSocketService } from '../modules/citas/services/eventos-socket.service'
import { NotificacionesTurnosService } from '../modules/citas/services/notificaciones-turnos.service'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environmentURL.apiBaseUrl
  message: string

  constructor (
    private http: HttpClient, 
    private router: Router,
    private inactivityTimeoutService: InactivityTimeoutService,
    private eventosSocket: EventosSocketService,
    private notificacionesTurnos: NotificacionesTurnosService,
  ) {}

  private getMessage (error: string): string {
    const messageObj: iMessage | undefined = message.find(
      msg => msg.error === error
    )
    return messageObj ? messageObj.message : 'Mensaje predeterminado'
  }

  /**
   * @param opts.userType Si es `USER_TYPE_INTERNO_SDM`, el backend autentica contra `sdm_sys_usuarios_internos` (solo web).
   */
  login (
    email: string,
    password: string,
    opts?: { userType?: string }
  ): Observable<{ success: boolean; mensaje:string; error:string; nombre: string; correo: string; token: string; usuario_id: string; role: string; maestro_id: string }> {
    const body: Record<string, string> = { email, password }
    if (opts?.userType) {
      body['user_type'] = opts.userType
    }
    return this.http
      .post<{ mensaje: string; error: string; token: string; usuario_id: string; nombre: string; correo: string; role: string; maestro_id: string }>(`${this.apiUrl}auth/login`, body)
      .pipe(
        map(response => {
          return { success: true, mensaje: response.mensaje, error: response.error, token: response.token, usuario_id: response.usuario_id, nombre: response.nombre, correo:response.correo, role: response.role, maestro_id: response.maestro_id }
        }),
        catchError(error => {

          // console.log("Autenticación error : ", error);
          return of({ success: false, mensaje: error.error.mensaje, error: error.error.error, token: '', usuario_id: '', nombre: '', correo: '', role: '', maestro_id:'' })
        })
      )
  }

  register (
    email: string,
    password: string,
    display: string
  ): Observable<boolean> {
    return from(bcrypt.hash(password, 10)).pipe(
      switchMap(hashedPassword =>
        this.http.post<{ message: string }>(`${this.apiUrl}auth/register`, {
          email: email,
          password: hashedPassword,
          display_name: display
        })
      ),
      map(response => true),
      catchError(error => {
        // console.error('Register error:', error)
        return of(false) // Devuelve false en caso de error
      })
    )
  }

  resetPassword (email: string): Observable<string> {
    return this.http
      .post<{ message: string }>(`${this.apiUrl}reset-password`, { email })
      .pipe(
        map(response => response.message),
        catchError(error => {
          // console.error('Reset password error:', error)
          return of('Error al enviar el correo de restablecimiento') // Devuelve un mensaje de error
        })
      )
  }

  logout (): Promise<void> {
    return new Promise((resolve) => {
      // Token primero para que otras pestañas detecten cierre de sesión vía storage
      localStorage.removeItem('token')
      localStorage.removeItem('role')
      localStorage.removeItem('nombre')
      localStorage.removeItem('correo')
      localStorage.removeItem('maestro_id')
      localStorage.removeItem('usuario_id')
      this.inactivityTimeoutService.clearTimeoutData()
      this.eventosSocket.desconectar()
      this.notificacionesTurnos.reiniciarSesion()
      resolve()
      // Ir a login (no a /inicio): si ya estás en inicio, navegar al mismo sitio no re-ejecuta el AuthGuard.
      this.router.navigateByUrl('/authentication/login', { replaceUrl: true })
    })
  }

  isAuthenticated (): Observable<boolean> {
    const token = localStorage.getItem('token')
    return of(!!token) // Aquí podrías hacer una validación más compleja si es necesario
  }

  /**
   * Persiste datos de sesión y reinicia timer/socket sin navegar.
   * Usado en login inicial (antes de navigate) y en reautenticación.
   */
  applySession (
    token: string,
    usuario_id: string,
    nombre: string,
    correo: string,
    role: string,
    maestro_id: string
  ): void {
    localStorage.setItem('token', token)
    localStorage.setItem('nombre', nombre)
    localStorage.setItem('correo', correo)
    localStorage.setItem('role', role)
    localStorage.setItem('maestro_id', maestro_id)
    localStorage.setItem('usuario_id', usuario_id)

    this.inactivityTimeoutService.startInactivityTimer()
    this.notificacionesTurnos.reiniciarSesion()
    this.eventosSocket.conectar()
  }

  // Genera la sesión del usuario y redirige al inicio
  async startSession ( token: string, usuario_id: string, nombre: string, correo: string, role: string, maestro_id: string ): Promise<void> {
    try {
      this.applySession(token, usuario_id, nombre, correo, role, maestro_id)
      this.router.navigate(['/inicio'])
    } catch (error: any) {
      this.message = this.getMessage(error.message || 'Error desconocido')
      throw new Error(this.message)
    }
  }

  /**
   * Renueva la sesión tras timeout de inactividad sin salir de la ruta actual.
   */
  reauthenticate (password: string): Observable<{ success: boolean; error: string }> {
    const correo = (localStorage.getItem('correo') || '').trim()
    if (!correo) {
      return of({ success: false, error: 'No se encontró el usuario de la sesión. Cierre sesión e inicie de nuevo.' })
    }
    return this.login(correo, password, { userType: USER_TYPE_INTERNO_SDM }).pipe(
      map(response => {
        if (!response.success) {
          return {
            success: false,
            error: response.error || response.mensaje || 'Contraseña incorrecta.',
          }
        }
        const maestroId =
          response.maestro_id != null && response.maestro_id !== undefined
            ? String(response.maestro_id)
            : (localStorage.getItem('maestro_id') || '')
        this.applySession(
          response.token,
          String(response.usuario_id),
          response.nombre,
          response.correo || correo,
          response.role,
          maestroId
        )
        this.inactivityTimeoutService.completeReauth()
        return { success: true, error: '' }
      }),
      catchError(() => of({ success: false, error: 'Error de conexión o del servidor.' }))
    )
  }

  // GUARDA NUEVO REGISTRO
  async registerAccount (
    email: string,
    password: string,
    display: string
  ): Promise<void> {
    try {
      const result = await this.register(email, password, display).toPromise()
      if (result) {
        this.router.navigate(['/authentication/login'])
      } else {
        throw new Error('No se pudo registrar el usuario')
      }
    } catch (error: any) {
      this.message = this.getMessage(error.message || 'Error desconocido')
      throw new Error(this.message)
    }
  }

  // ENVIA CORREO PARA RESETEAR LA CONTRASEÑA
  resetPasswordHandler (email: string): Observable<string> {
    try {
      const message = this.resetPassword(email)
      return message
    } catch (error: any) {
      this.message = this.getMessage(error.message || 'Error desconocido')
      throw new Error(this.message)
    }
  }
}
